<template>
    <div v-if="!isLoading">
        <panel :title="$t('str.form.title.settings')">
            <form>
                <fieldset>
                    <!-- Configurações do Modo Escuro -->
                    <div class="row mt-10px">
                        <div class="col-8 control-label fw-bold">
                            <label class="form-label">{{ $t('str.dark.mode') }}</label>
                            <div class="lh-14">
                                <small class="text-dark opacity-50">{{ $t('str.dark.mode.description') }}</small>
                            </div>
                        </div>
                        <div class="col-4 d-flex">
                            <div class="form-check form-switch ms-auto mb-0">
                                <input type="checkbox" class="form-check-input" name="app-theme-dark-mode" id="appThemeDarkMode" @change="handleSetDarkMode" v-model="darkMode" />
                                <label class="form-check-label" for="appThemeDarkMode">&nbsp;</label>
                            </div>
                        </div>
                    </div>

                    <hr class="my-3" />

                    <div v-if="showMonitorEventFiltersSettings" class="row mt-10px">
                        <div class="col-8 control-label fw-bold">
                            <label class="form-label">{{ $t('str.settings.monitor.event.filters.title') }}</label>
                            <div class="lh-14">
                                <small class="text-dark opacity-50">{{ $t('str.settings.monitor.event.filters.description') }}</small>
                            </div>
                        </div>
                        <div class="col-4 d-flex align-items-start justify-content-end">
                            <small v-if="isSavingCompanySettings" class="text-muted">
                                <i class="fas fa-spinner fa-spin me-1" />
                                {{ $t('str.settings.monitor.event.filters.loading') }}
                            </small>
                        </div>
                    </div>

                    <div v-if="showMonitorEventFiltersSettings" class="row mt-3">
                        <div class="col-12">
                            <div v-if="isLoadingCompanySettings" class="text-muted small">
                                <i class="fas fa-spinner fa-spin me-1" />
                                {{ $t('str.settings.monitor.event.filters.loading') }}
                            </div>
                            <div v-else>
                                <div class="row g-2">
                                    <div v-for="item in monitorEventFilters" :key="item.type" class="col-3 mb-3">
                                        <div
                                            :class="[
                                                'monitor-toggle-item d-flex align-items-center gap-2 h-100',
                                                { 'monitor-toggle-item-clickable': canEditMonitorEventFilters && !isSavingCompanySettings && hasCompanySettingsAccount },
                                            ]"
                                        >
                                            <div class="control-label fw-bold monitor-toggle-label">
                                                <label class="form-label mb-0" :for="`monitor-filter-${item.type}`">{{ $t(item.label) }}</label>
                                            </div>
                                            <div class="form-check form-switch monitor-toggle-switch mb-0">
                                                <input
                                                    :id="`monitor-filter-${item.type}`"
                                                    class="form-check-input"
                                                    type="checkbox"
                                                    :checked="item.enabled"
                                                    :disabled="!canEditMonitorEventFilters || isSavingCompanySettings || !hasCompanySettingsAccount"
                                                    @change="handleMonitorEventFilterChange(item.type, $event.target.checked)"
                                                />
                                                <label class="form-check-label" :for="`monitor-filter-${item.type}`">&nbsp;</label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <hr v-if="showMonitorEventFiltersSettings" class="my-3" />

                    <div v-if="showMonitorEventFiltersSettings" class="row mt-10px">
                        <div class="col-8 control-label fw-bold">
                            <label class="form-label">{{ $t('str.settings.monitor.call.recording.title') }}</label>
                            <div class="lh-14">
                                <small class="text-dark opacity-50">{{ $t('str.settings.monitor.call.recording.description') }}</small>
                            </div>
                        </div>
                        <div class="col-4 d-flex align-items-center justify-content-end">
                            <div class="form-check form-switch monitor-toggle-switch mb-0">
                                <input
                                    id="monitor-call-recording-enabled"
                                    class="form-check-input"
                                    type="checkbox"
                                    :checked="companySettings?.monitor?.callRecordingEnabled === true"
                                    :disabled="!canEditMonitorEventFilters || isSavingCompanySettings || !hasCompanySettingsAccount"
                                    @change="handleCallRecordingToggle($event.target.checked)"
                                />
                                <label class="form-check-label" for="monitor-call-recording-enabled">&nbsp;</label>
                            </div>
                        </div>
                    </div>
                </fieldset>
            </form>
        </panel>
    </div>
    <div v-else class="center-spinner">
        <i class="fas fa-spinner fa-spin" />
    </div>
</template>

<script>
import Controller from './CrtSettings.vue'
import Vue from 'vue'
import { createDefaultMonitorEventFilters, getDefaultMonitorEventFilterOptions } from '../../utils/events'

Vue.prototype.$registerEvent = new Vue()

export default {
    name: 'Settings',
    data() {
        return {
            isLoading: true,
            errors: [],
            company: {},
            user: null,
            role: '',
            darkMode: false,
            companySettings: null,
            monitorEventFilterOptions: getDefaultMonitorEventFilterOptions(),
            monitorEventFilters: createDefaultMonitorEventFilters(),
            isLoadingCompanySettings: false,
            isSavingCompanySettings: false,
        }
    },
    computed: {
        canEditMonitorEventFilters() {
            return this.role === 'ADMIN'
        },
        hasCompanySettingsAccount() {
            return Boolean(this.companySettings?.account || this.user?.account?._id)
        },
        showMonitorEventFiltersSettings() {
            return this.role === 'ADMIN'
        },
    },
    methods: {
        ...Controller.methods,
    },
    mounted() {
        Controller.init(this)
    },
}
</script>

<style scoped>
.monitor-toggle-item {
    padding: 0.65rem 0.85rem;
    min-height: 56px;
    border-radius: 12px;
    background: rgba(var(--app-component-color-rgb), 0.09);
}

.monitor-toggle-label {
    line-height: 1.2;
}

.monitor-toggle-switch {
    display: inline-flex;
    align-items: center;
}

.monitor-toggle-switch .form-check-input {
    margin-top: 0;
}

.monitor-toggle-item-clickable .form-label,
.monitor-toggle-item-clickable .form-check-input,
.monitor-toggle-item-clickable .form-check-label {
    cursor: pointer;
}
</style>
